import * as React from 'react';
import { useLocation, useParams } from 'react-router';
import styled from 'styled-components';

import { useMutation } from '@apollo/client';
import { GetProperty } from '../../../../__types__/generated/GetProperty';
import {
  Valuation,
  ValuationVariables,
} from '../../../../__types__/generated/Valuation';
import { VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';

import { Button } from '../../../ui/Button';
import { FormError } from '../../../ui/FormError';
import { Input } from '../../../ui/Input';
import { useForm } from '../../../utils/hooks/useForm';
import { BrokerSoldItem } from './BrokerSoldItem';
import { GetPropertyTop } from '../../../../__types__/generated/GetPropertyTop';

export const SoldPriceForm: React.FC<{ data?: GetPropertyTop }> = ({
  data,
}) => {
  const params = useParams<{ sguid: string }>();
  const location = useLocation();
  const [posted, setPosted] = React.useState(false);

  const broker1 =
    data?.getProperty?.brokers?.list && data?.getProperty?.brokers?.list[0];

  const [post, { loading, error }] = useMutation<Valuation, ValuationVariables>(
    VALUATION_MUTATION,
    {
      onCompleted: ({ lead }) => {
        if (lead?.valuation?.success) {
          setPosted(true);
        }
      },
    }
  );

  const { fields, handleChange, handleSubmit } = useForm({
    fields: [
      {
        type: 'text',
        label: 'Postnr. *',
        name: 'zip',
        placeholder: 'Eks. 0000',
        value: '',
        required: true,
      },
      {
        type: 'tel',
        label: 'Telefon *',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        required: true,
      },
    ],
    submit: (fields) => {
      post({
        variables: {
          input: {
            identifier: broker1?.path,
            referer: `${location.pathname}${location.search}`,
            leadType: 'VERDIVURDERING',
            pageType: 'MEGLER',
            ...fields,
          },
        },
      });
    },
  });

  return (
    <>
      <h1 className="property-title">
        PrivatMegleren {data?.getProperty?.office?.info?.name} har nylig solgt
        denne eiendommen.
      </h1>
      <h2 className="property-sold-description" style={{ color: '#fff' }}>
        Har du en bolig i dette område som vurderes solgt? Send inn skjemaet
        under eller kontakt gjerne megler som solgte denne eiendommen direkte,
        hvis ønskelig.
      </h2>
      <StyledSoldPriceFormWrapper>
        {!posted ? (
          <form onSubmit={handleSubmit}>
            {error?.graphQLErrors.map((item) => {
              return <FormError message={item.message} />;
            })}
            <div className="top">
              {fields.map((item, index: number) => {
                return (
                  <Input
                    key={`field_${index}`}
                    placeholder={item?.placeholder}
                    name={item?.name}
                    label={item?.label}
                    value={item?.value}
                    error={item?.error}
                    onChange={handleChange}
                  />
                );
              })}
            </div>
            <div className="bottom">
              <Button
                type="submit"
                variant="solid"
                loading={loading}
                colorVariant="primary"
              >
                Send skjema
              </Button>
            </div>
          </form>
        ) : (
          <FormSuccess>
            <h2>Takk for din henvendelse</h2>
            <p>En megler vil kontakte deg innen kort tid.</p>
          </FormSuccess>
        )}
        <BrokerWrapper className="hide-mobile">
          {broker1 ? (
            <BrokerSoldItem
              image={broker1?.image?.full}
              name={broker1?.name}
              title={broker1?.title}
              phone={broker1?.cellphone}
              path={broker1?.path}
              office={broker1?.office?.info?.name}
              urltag={broker1?.office?.info?.urltag}
            />
          ) : null}
        </BrokerWrapper>
        {data?.getProperty?.office?.info?.hasSoldPrice ? (
          <div style={{ width: '100%', marginTop: '10px' }}>
            <Button
              variant="solid"
              colorVariant="primary"
              onClick={() => {
                window.open(
                  `https://privatmegleren.no/${data?.getProperty?.office?.info?.urltag}/${params.sguid}/${data.getProperty?.permalink}/solgtpris`
                );
              }}
            >
              Fortell meg hva eiendommen ble solgt for
            </Button>
          </div>
        ) : null}
      </StyledSoldPriceFormWrapper>
    </>
  );
};

const StyledSoldPriceFormWrapper = styled.div`
  width: 100%;

  .top {
    margin-bottom: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 10px;
  }

  .bottom {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const BrokerWrapper = styled.div`
  margin-top: 10px;

  @media all and (max-width: 960px) {
    &.hide-mobile {
      display: none;
    }
  }
`;

const FormSuccess = styled.div`
  padding: 20px 0;
  text-align: center;

  h2 {
    margin-bottom: 0;
  }
  p {
    margin-top: 0;
  }
`;
